import PropTypes from 'prop-types';
import { useContext } from 'react';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import AppContext from '../../context/AppContext';
import HeaderNotice from '../../components/Header/HeaderNotice';
import RecipeLayout from '../../components/Layout/RecipeLayout';
import SeoYoast from '../../components/Seo/SeoYoast';
import SingleRecipe from '../../components/SingleRecipe/SingleRecipe';
import SingleRecipeJsonLd from '../../components/SingleRecipe/SingleRecipeJsonLd';
import SeoJsonLdVideo from '../../components/Seo/Json/SeoJsonLdVideo';
import { SINGLE_RECIPE } from '../../components/SingleRecipe/SingleRecipeQuery';
import { isGeoBlocked, REDIRECT_URL } from '../../utils/geoBlock';
import { initializeApollo } from '../../utils/apolloClient';
import OutbrainAd from '../../components/Ad/OutbrainAd';
import { isVideoShortcode, isFrenchPage } from '../../utils/utility';
import {
	NS_COMMON, NS_ARTICLE, NS_RATINGS, NS_RECIPE,
} from '../../utils/lang';

import AdobeAnalyticsDataLayer from '../../components/AdobeAnalytics/AdobeAnalyticsDataLayer';

// Global Data
import globalValues from '../../static-data/global-values.json';

const isOutbrainEnabled = globalValues?.data?.corusAdsSettings?.ceAdsOutbrainEnabled;

const outbrainWidgetId = globalValues?.data?.corusAdsSettings?.ceAdsOutbrainArticleWidgetId;

export default function Recipe(props) {
	const { recipe } = props;
	const { hostUrl } = useContext(AppContext);

	const isShowTag = recipe?.featuredTag?.recipeDetailFeaturedTag?.showTag?.showTag || false;
	const tagType = (isShowTag) ? 'show' : '';

	const recipeCategory = recipe?.featuredTag?.recipeDetailFeaturedTag?.slug;
	const recipeTags = recipe?.tags?.nodes?.map((node) => node.slug) ?? [];
	const recipeID = recipe?.databaseId;
	const recipeType = 'recipe';
	const recipeCreatedDate = recipe?.date;
	const recipePublishedDate = recipe?.date;
	const recipeModifiedDate = recipe?.modified;
	const recipeTitle = recipe?.title;
	const recipeAuthors = recipe?.additionalDetails?.authors;
	const recipeHasVideo = !!recipe?.additionalDetails?.featuredVideoId || isVideoShortcode(recipe?.content);
	const recipeRating = parseFloat(recipe?.recipeDetails?.recipeDetailRatingGroup?.recipeDetailRatingAvgScore);
	const recipeReviews = parseFloat(recipe?.recipeDetails?.recipeDetailRatingGroup?.recipeDetailRatingNumVotes);
	const recipeLengthHours = recipe?.recipeDetails?.recipeDetailTimesGroup?.recipeDetailPrepTimeGroup?.recipeDetailPrepTimeHours;
	const recipeLengthMin = recipe?.recipeDetails?.recipeDetailTimesGroup?.recipeDetailPrepTimeGroup?.recipeDetailPrepTimeMinutes;
	const recipeLength = parseFloat([recipeLengthHours, recipeLengthMin].join(''));

	return (
		<>
			<AdobeAnalyticsDataLayer
				viewType="content"
				contentID={recipeID}
				contentType={recipeType}
				contentCreatedDate={recipeCreatedDate}
				contentPublishedDate={recipePublishedDate}
				contentModifiedDate={recipeModifiedDate}
				contentTitle={recipeTitle}
				contentTags={recipeTags}
				contentCategory={recipeCategory}
				contentAuthors={recipeAuthors}
				contentHasVideo={recipeHasVideo}
				contentRating={recipeRating}
				contentRecipeLength={recipeLength}
				contentReviews={recipeReviews}
			/>
			<SeoYoast seo={recipe?.seo} />
			{/* Google Recipe Cards */}
			<SingleRecipeJsonLd recipe={recipe} siteUrl={hostUrl} key="recipe-ld-json" />
			<SeoJsonLdVideo post={recipe} />
			<SingleRecipe recipe={recipe} />
		</>
	);
}

Recipe.layout = ({ ...props }) => (
	<RecipeLayout
		{...props}
		preHeaderContent={() => {
			const { t, i18n } = useTranslation(NS_COMMON);
			const currentLanguage = i18n.language;

			return (
				isFrenchPage(currentLanguage) ? (
					<HeaderNotice noticeMessage={t('headerNotice')} />
				) : (
					<></>
				)
			);
		}}
		preFooterContent={() => {
			const { hostUrl } = useContext(AppContext);

			return (
				isOutbrainEnabled ? (
					<div className="content-well recipe-outbrain">
						<OutbrainAd src={hostUrl} widgetId={outbrainWidgetId} />
					</div>
				) : (
					<></>
				)
			);
		}}
	/>
);

export async function getServerSideProps(context) {
	const apolloClient = initializeApollo();

	const {
		params: { recipeSlug },
	} = context;

	const recipe = await apolloClient.query({
		query: SINGLE_RECIPE,
		errorPolicy: 'all',
		variables: {
			id: recipeSlug,
			idType: 'SLUG',
		},
	});

	if (recipe && recipe.data?.recipe) {
		// Restrict US content from US users
		if (isGeoBlocked(recipe.data.recipe, context)) {
			// Redirect to homepage
			return {
				redirect: {
					permanent: false,
					destination: REDIRECT_URL,
				},
				props: {},
			};
		}

		const locale = recipe.data.recipe.recipeFrench.recipeIsFrench ? 'fr' : 'en';
		const translations = await serverSideTranslations(locale, [
			NS_COMMON, NS_ARTICLE, NS_RATINGS, NS_RECIPE,
		]);

		return {
			props: {
				recipe: recipe.data?.recipe,
				...translations,
			},
		};
	}

	return { notFound: true };
}

Recipe.propTypes = {
	recipe: PropTypes.object.isRequired,
};
